import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DocumentManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortBy, setSortBy] = useState('date');

  const documents = [
    {
      id: 1,
      name: "Form 16 - December 2024",
      type: "TDS Certificate",
      category: "tds",
      size: "2.4 MB",
      uploadDate: "2025-01-15",
      status: "approved",
      downloadCount: 45,
      generatedBy: "System Auto"
    },
    {
      id: 2,
      name: "PF Challan - January 2025",
      type: "Challan",
      category: "pf",
      size: "1.8 MB",
      uploadDate: "2025-01-20",
      status: "pending",
      downloadCount: 12,
      generatedBy: "HR Admin"
    },
    {
      id: 3,
      name: "ESI Return - Q3 2024",
      type: "Statutory Return",
      category: "esi",
      size: "3.2 MB",
      uploadDate: "2025-01-18",
      status: "approved",
      downloadCount: 28,
      generatedBy: "System Auto"
    },
    {
      id: 4,
      name: "Professional Tax Receipt",
      type: "Payment Receipt",
      category: "pt",
      size: "856 KB",
      uploadDate: "2025-01-19",
      status: "approved",
      downloadCount: 8,
      generatedBy: "Finance Team"
    },
    {
      id: 5,
      name: "Audit Trail Report - January",
      type: "Audit Report",
      category: "audit",
      size: "4.1 MB",
      uploadDate: "2025-01-21",
      status: "draft",
      downloadCount: 3,
      generatedBy: "Compliance Officer"
    }
  ];

  const documentTypes = [
    { value: '', label: 'All Documents' },
    { value: 'tds', label: 'TDS Documents' },
    { value: 'pf', label: 'PF Documents' },
    { value: 'esi', label: 'ESI Documents' },
    { value: 'pt', label: 'Professional Tax' },
    { value: 'audit', label: 'Audit Reports' }
  ];

  const sortOptions = [
    { value: 'date', label: 'Upload Date' },
    { value: 'name', label: 'Document Name' },
    { value: 'type', label: 'Document Type' },
    { value: 'downloads', label: 'Download Count' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'text-success bg-success/10 border-success/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      case 'draft':
        return 'text-muted-foreground bg-muted border-border';
      case 'rejected':
        return 'text-error bg-error/10 border-error/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'tds':
        return 'Calculator';
      case 'pf':
        return 'Banknote';
      case 'esi':
        return 'Heart';
      case 'pt':
        return 'FileText';
      case 'audit':
        return 'Search';
      default:
        return 'File';
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = !filterType || doc.category === filterType;
    return matchesSearch && matchesFilter;
  });

  const handleDownload = (docId, docName) => {
    console.log(`Downloading document: ${docName}`);
  };

  const handlePreview = (docId, docName) => {
    console.log(`Previewing document: ${docName}`);
  };

  const handleDelete = (docId, docName) => {
    console.log(`Deleting document: ${docName}`);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="FolderOpen" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Document Management</h3>
            <p className="text-sm text-muted-foreground">Manage compliance documents and reports</p>
          </div>
        </div>
        <Button variant="default" iconName="Upload" iconPosition="left">
          Upload Document
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Input
          type="search"
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        
        <Select
          placeholder="Filter by type"
          options={documentTypes}
          value={filterType}
          onChange={setFilterType}
        />
        
        <Select
          placeholder="Sort by"
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
        />
      </div>

      {/* Documents List */}
      <div className="space-y-3">
        {filteredDocuments.map((doc) => (
          <div key={doc.id} className="p-4 border border-border rounded-lg hover:shadow-sm transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                  <Icon name={getCategoryIcon(doc.category)} size={20} className="text-muted-foreground" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h4 className="font-medium text-foreground">{doc.name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(doc.status)}`}>
                      {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{doc.type}</span>
                    <span>•</span>
                    <span>{doc.size}</span>
                    <span>•</span>
                    <span>Uploaded: {new Date(doc.uploadDate).toLocaleDateString('en-IN')}</span>
                    <span>•</span>
                    <span>{doc.downloadCount} downloads</span>
                  </div>
                  
                  <p className="text-xs text-muted-foreground mt-1">
                    Generated by: {doc.generatedBy}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Eye"
                  onClick={() => handlePreview(doc.id, doc.name)}
                >
                  Preview
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Download"
                  onClick={() => handleDownload(doc.id, doc.name)}
                >
                  Download
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => handleDelete(doc.id, doc.name)}
                  className="text-error hover:text-error hover:bg-error/10"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredDocuments.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileX" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No documents found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Storage Info */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <div className="text-muted-foreground">
            {filteredDocuments.length} documents • Total size: 12.3 MB
          </div>
          <div className="flex items-center gap-4">
            <span className="text-muted-foreground">Storage used: 45.2 MB / 1 GB</span>
            <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
              <div className="w-1/4 h-full bg-primary rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentManagement;